import { memo } from "react";

import { RichText } from "@shopify/hydrogen-react";

import { Heading } from "@components/Heading";
import { Image } from "@components/Image";
import { Link } from "@components/Link";
import { Text } from "@components/Text";
import { type CollectionContentCard } from "@root/tablescape/state";
import { tw } from "classnames";

type Props = Pick<
  CollectionContentCard,
  "title" | "description" | "background" | "ctaUrl" | "image" | "mobileImage"
>;

const styles = {
  base: ({ background }: { background: string }) =>
    tw("h-full", "no-underline", {
      [tw("bg-background-secondary", "text-primary")]: background === "bone",
      [tw("bg-sage-700", "text-white")]: background === "sage",
      [tw("bg-char-900", "text-white")]: background === "char",
      [tw("bg-interactive-brick", "text-white")]: background === "brick",
    }),
  imageOuterWrapper: tw(
    "block",
    "overflow-hidden",
    "before:float-left",
    "before:-ml-1",
    "before:w-1",
    "before:pt-full",
    "after:clear-both",
    "after:table",
    "md:before:h-0",
    "md:before:content-['']",
    "md:after:content-['']"
  ),
  imageInnerWrapper: tw("h-60", "w-full", "h-full"),
  image: tw("object-cover", "h-full", "w-full", "md:block", "hidden"),
  mobileImage: tw("md:hidden", "max-h-60", "w-full", "object-cover"),
  content: tw("flex", "flex-1", "flex-col", "p-6", "py-4", "md:py-6"),
  title: tw("mb-2"),
};

export const CollectionImageTextCardBase = ({
  background = "bone",
  ctaUrl,
  description,
  image,
  mobileImage,
  title,
}: Props) => {
  const Tag = ctaUrl ? Link : "div";
  return (
    <Tag
      className={styles.base({ background })}
      {...(ctaUrl ? { to: ctaUrl, unstyled: true } : { to: "" })}
    >
      <div className={styles.imageOuterWrapper}>
        <div className={styles.imageInnerWrapper}>
          {image && (
            <Image
              className={styles.image}
              data={image.image}
              height={352}
              width={352}
            />
          )}
          {(mobileImage || image) && (
            <Image
              className={styles.mobileImage}
              data={mobileImage?.image ?? image?.image}
              height={240}
              width={328}
            />
          )}
        </div>
      </div>
      <div className={styles.content}>
        {title && (
          <RichText
            className={styles.title}
            components={{
              paragraph({ node }) {
                return <Heading as="h6">{node.children}</Heading>;
              },
            }}
            data={title}
          />
        )}
        {description && (
          <RichText
            components={{
              paragraph({ node }) {
                return <Text as="body2">{node.children}</Text>;
              },
            }}
            data={description}
          />
        )}
      </div>
    </Tag>
  );
};

export const CollectionImageTextCard = memo(CollectionImageTextCardBase);
